import React, { useEffect, useState, useCallback, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton,
    Alert,
    Box,
    Typography,
    Chip,
    Checkbox,
    InputAdornment,
} from "@mui/material";
import { Add, Search } from "@mui/icons-material";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import debounce from "lodash.debounce";
import { useCurrentUser } from "../utils/useCurrentUser";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const GroupUserAssignmentManager = ({ open, onClose, groupId, groupName }) => {
    const { userId, empId, boId, roles, hasPermission } = useCurrentUser();
    const [assignedUsers, setAssignedUsers] = useState([]);
    const [loadingAssigned, setLoadingAssigned] = useState(false);

    const [searchResults, setSearchResults] = useState([]);
    const [loadingSearch, setLoadingSearch] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [alert, setAlert] = useState({ type: "", msg: "" });

    const assignedUsersRef = useRef([]);
    useEffect(() => {
        assignedUsersRef.current = assignedUsers;
    }, [assignedUsers]);

    // ✅ Fetch assigned users for this group
    const refreshAssignedUsers = useCallback(async () => {
        if (!groupId) return;
        setLoadingAssigned(true);
        try {
            const res = await fetch(`${BASE_URL}/api/groupUsers/${groupId}`);
            const data = await res.json();
            setAssignedUsers(data || []);
        } catch (err) {
            console.error("Failed to fetch assigned users:", err);
            setAlert({ type: "error", msg: "Failed to load assigned users" });
        } finally {
            setLoadingAssigned(false);
        }
    }, [groupId]);

    useEffect(() => {
        if (open) {
            refreshAssignedUsers();
            setSearchResults([]);
            setSearchQuery("");
            setSelectedUsers([]);
        }
    }, [open, refreshAssignedUsers]);

    // Auto-clear alert
    useEffect(() => {
        if (!alert.msg) return;
        const timer = setTimeout(() => setAlert({ type: "", msg: "" }), 6000);
        return () => clearTimeout(timer);
    }, [alert.msg]);

    // ✅ Search users
    const handleSearch = useCallback(
        debounce(async (query) => {
            if (!query?.trim()) {
                setSearchResults([]);
                return;
            }
            setLoadingSearch(true);
            try {
                const res = await fetch(
                    `${BASE_URL}/api/groupUsers/${groupId}/search?search=${query}`
                );
                const data = await res.json();
                const users = data?.data || [];
                const assignedIds = new Set(assignedUsersRef.current.map((u) => u.user_id));
                const filtered = users.filter((u) => !assignedIds.has(u.user_id));
                setSearchResults(filtered);
            } catch (err) {
                console.error("Search failed:", err);
            } finally {
                setLoadingSearch(false);
            }
        }, 300),
        [groupId]
    );

    // ✅ Assign user to group
    const handleAssignUser = async (user) => {
        try {
            const response = await fetch(
                `${BASE_URL}/api/groupUsers/${groupId}/${user.user_id}`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ boId, empId }),
                }
            );

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData?.message || `Failed with ${response.status}`);
            }

            setAlert({ type: "success", msg: `Added ${user.name} to ${groupName}` });
            refreshAssignedUsers();
            setSearchResults([]);
            setSearchQuery("");
        } catch (err) {
            console.error("Assign failed:", err);
            setAlert({ type: "error", msg: err.message || "Failed to add user" });
        }
    };

    const handleAssignSelectedUsers = async () => {
        for (const user of selectedUsers) {
            await handleAssignUser(user);
        }
        setSelectedUsers([]);
    };

    // ✅ Remove user from group
    const handleRemove = async (user) => {
        try {
            const response = await fetch(
                `${BASE_URL}/api/groupUsers/${groupId}/users/${user.user_id}`,
                { method: "DELETE" }
            );

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData?.message || `Failed with ${response.status}`);
            }

            setAlert({
                type: "success",
                msg: `${user.name} removed from ${groupName}`,
            });
            refreshAssignedUsers();
        } catch (err) {
            console.error("Remove failed:", err);
            setAlert({ type: "error", msg: err.message || "Failed to remove user" });
        }
    };

    const handleSelectUser = (user, isSelected) => {
        setSelectedUsers((prev) =>
            isSelected ? [...prev, user] : prev.filter((u) => u.user_id !== user.user_id)
        );
    };

    return (
        <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
            <DialogTitle>
                Manage Users for - <span>{groupName || ""}</span>
            </DialogTitle>
            <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 3, maxHeight: "70vh" }}>
                {alert.msg && (
                    <Alert severity={alert.type} onClose={() => setAlert({ type: "", msg: "" })}>
                        {alert.msg}
                    </Alert>
                )}

                {/* Search Users Section */}
                <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 1 }}>
                        <Typography variant="subtitle1">Search & Add Users</Typography>
                        <TextField
                            size="small"
                            sx={{ width: "250px", height: 34 }}
                            placeholder="Search users..."
                            variant="outlined"
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                handleSearch(e.target.value);
                            }}
                            InputProps={{
                                sx: { height: 36, paddingRight: 0 },
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                    {/* Selected Users Preview */}
                    {selectedUsers.length > 0 && (
                        <Box sx={{ mt: 1, mb: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {selectedUsers.map((user) => (
                                <Chip
                                    key={user.user_id}
                                    label={user.name}
                                    onDelete={() => handleSelectUser(user, false)}
                                    color="primary"
                                />
                            ))}
                            <Button
                                variant="contained"
                                size="small"
                                sx={{
                                    fontWeight: "bold",
                                    borderRadius: "8px",
                                    px: 2.5,
                                    height: 36,
                                    backgroundColor: "#2DB0E4",
                                }}
                                startIcon={<Add />}
                                onClick={handleAssignSelectedUsers}
                            >
                                Add Selected
                            </Button>
                        </Box>
                    )}

                    {/* Search Results */}
                    {searchResults.length > 0 && (
                        <Table size="small" sx={{ mt: 1 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox" />
                                    <TableCell>Employee ID</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Email</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {searchResults.map((user) => (
                                    <TableRow key={user.user_id}>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selectedUsers.some((u) => u.user_id === user.user_id)}
                                                onChange={(e) => handleSelectUser(user, e.target.checked)}
                                            />
                                        </TableCell>
                                        <TableCell>{user.employee_id}</TableCell>
                                        <TableCell>{user.name}</TableCell>
                                        <TableCell>{user.email || "—"}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </Box>

                {/* Assigned Users Section */}
                <Box sx={{ flex: 1, overflowY: "auto", maxHeight: 400, position: "relative" }}>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            position: "sticky",
                            top: 0,
                            backgroundColor: "background.paper",
                            zIndex: 2,
                            py: 1,
                        }}
                    >
                        Assigned Users
                    </Typography>
                    {loadingAssigned ? (
                        <CircularProgress size={24} />
                    ) : assignedUsers.length === 0 ? (
                        <p align="center">No users assigned</p>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell align="left">Action</TableCell>
                                    <TableCell>Employee ID</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Email</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {assignedUsers.map((user, idx) => (
                                    <TableRow key={`${user.user_id}-${idx}`}>
                                        <TableCell align="left">
                                            <IconButton color="error" onClick={() => handleRemove(user)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>{user.employee_id || "—"}</TableCell>
                                        <TableCell>{user.name}</TableCell>
                                        <TableCell>{user.email || "—"}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose(false)}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default GroupUserAssignmentManager;
